import { STATUS_GRID_OPTIONS, TRANS_TYPE_OPTIONS } from "./constants";

/*
 * Column definitions for the Preview grid — the Delphi where-builder's preview of WMTRANSACTIONHEADER
 * (btnPreviewClick -> wbWMReplicate.Preview).
 *
 * WHICH COLUMNS: taken from the Delphi preview grid, not guessed. That grid is built by
 * TAbWhereBuilder.Preview -> CreateVisibleColumns, and the deciding flag is VisibleOnGrid, NOT Visible:
 * Preview copies VisibleOnGrid onto each cloned field but never copies Visible (uAbWhereBuilder.pas),
 * so the clone's Visible is always the default True and CreateVisibleColumns' "VisibleOnGrid and
 * Visible" test (AbReadOnlyGrid.pas) reduces to VisibleOnGrid. Decoded per field from the DFM binary:
 *
 *   VisibleOnGrid = True  -> Record #, Customer ID, Transaction #, Trans Date, Status, Shippers #,
 *                            PO/Reference, Transaction Group, Trans Type
 *   VisibleOnGrid = False -> Stage, Created By, Last Updated By, Department, ROWID
 *
 * Note Transaction Group and Trans Type carry Visible = False in the DFM, which is inert here — they ARE
 * in the AI8 preview. Created By / Last Updated By / Department are filterable but NOT preview columns,
 * an asymmetry in the Delphi dataset that is reproduced rather than corrected; they ship visible:false so
 * a user who filtered on them can still reach them through the column chooser.
 *
 * Record # is lifted to the front (Architecture.md rule 10); otherwise the order is the DFM field
 * declaration order.
 *
 * dataField casing is PascalCase off RecordsReadyToCommitDto, because the endpoint serialises with
 * DefaultContractResolver.
 *
 * dataType is declared on EVERY column, deliberately. DevExtreme infers it only from the first non-null
 * value, and Shippers #, PO/Reference, Transaction Group and Department are all frequently empty for a
 * whole result set. With nothing to infer from, the column gets no filter-row editor and the user
 * silently loses the ability to filter it.
 *
 * minWidth on every column keeps columnAutoWidth from compressing them into slivers.
 *
 * NO defaultSortOrder anywhere. The Delphi dataset's order is
 * "ORDER BY WMTRANSACTIONHEADER.KEYSEQUENCE" and RecordNumber is a base-31 STRING (FNAB_INTTOBASEN),
 * whose lexical order is NOT the numeric KEYSEQUENCE order. The server orders the rows; the grid must add
 * no sort of its own so that order shows through.
 */

const TEXT = "string";
const DATETIME = "datetime";

// Matches AI8, which renders TRANSACTIONDATE with the time: uWMM0094.dfm gives the field
// TimeStorageFormat / TimeDisplayFormat tfLocal. Unpadded, like the sibling screen's columns.
const DATETIME_FORMAT = "M/d/yyyy h:mm:ss a";

const W_SHORT = 90;
const W_CODE = 140;
const W_NAME = 180;
const W_DATETIME = 165;

export const PREVIEW_KEY_EXPR = "RowId";

const text = (dataField, caption, width = W_CODE) => ({
    dataField,
    caption,
    dataType: TEXT,
    minWidth: width,
});

export const PREVIEW_COLUMNS = [
    // Technical key — never shown, and not offered in the column chooser either (rule 8).
    { dataField: "RowId", dataType: TEXT, visible: false, showInColumnChooser: false },

    text("RecordNumber", "Record #", W_SHORT),

    // uWMM0094.dfm aliases IDMC.IDENTITYID as CustomerID; the DTO exposes it as IdentityId.
    text("IdentityId", "Customer ID"),
    text("TransactionNumber", "Transaction #"),
    {
        dataField: "TransactionDate",
        caption: "Trans Date",
        dataType: DATETIME,
        format: DATETIME_FORMAT,
        minWidth: W_DATETIME,
    },

    // Rule 3: coded fields render descriptively. STATUS is NOT pinned by the base filter (only STAGE,
    // TRANSACTIONTYPE and RECURRINGCHARGE are), so this column genuinely varies row to row. It is
    // excluded from FILTERING for a different reason — the screen's own Status option sets the status of
    // the GENERATED header, not the source.
    {
        ...text("Status", "Status", W_NAME),
        lookup: { dataSource: STATUS_GRID_OPTIONS, displayExpr: "text", valueExpr: "id" },
    },

    text("ShipperBolNumber", "Shippers #"),
    text("Reference", "PO/Reference", W_NAME),
    text("TransactionGroup", "Transaction Group", W_NAME),

    // Constant 'M' for every row (the base filter pins it), but it IS a Delphi preview column, so it is
    // shown — through a one-entry lookup rather than as a bare code.
    {
        ...text("TransactionType", "Trans Type", W_NAME),
        lookup: { dataSource: TRANS_TYPE_OPTIONS, displayExpr: "text", valueExpr: "id" },
    },

    // Created By, Last Updated By and Department are deliberately absent, not hidden. All three carry
    // VisibleOnGrid = False in the Delphi dataset, so they are not AI8 preview columns; they remain
    // available as FILTER fields, which needs no grid column. They were briefly shipped as
    // visible: false, which still listed them in the column chooser — team decision was to drop them
    // outright, which is also the more faithful reading of the DFM. The server no longer selects them.
];
